import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DynamicFields } from '../components/DynamicFields';
import {
  Button,
  Card,
  Field,
  Loading,
  Notice,
  PageHeader,
  StatusBadge,
} from '../components/ui';
import { api, jsonBody } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { ConnectivityType, StudyDetail } from '../lib/types';

type SaveIntent = 'draft' | 'complete';

interface TechnicalForm {
  weightKg: string;
  heightCm: string;
  systolicBloodPressure: string;
  diastolicBloodPressure: string;
  heartRateBpm: string;
  respiratoryRateBpm: string;
  temperatureC: string;
  patientCondition: string;
  relevantHistory: string;
  technicalObservations: string;
  procedureIncidents: string;
  studyStarted: boolean;
  studyCompleted: boolean;
  sourceFilesComplete: boolean;
  technicalConfirmation: boolean;
  deliveryComment: string;
  procedureStatus: string;
  nonPerformanceReason: string;
  startedAt: string;
  endedAt: string;
  connectivityType: ConnectivityType;
  profusionPatientCode: string;
  profusionStudyCode: string;
  dynamicValues: Record<string, unknown>;
  additionalFields: Array<{ title: string; description: string }>;
}
const blank: TechnicalForm = {
  weightKg: '',
  heightCm: '',
  systolicBloodPressure: '',
  diastolicBloodPressure: '',
  heartRateBpm: '',
  respiratoryRateBpm: '',
  temperatureC: '',
  patientCondition: '',
  relevantHistory: '',
  technicalObservations: '',
  procedureIncidents: '',
  studyStarted: false,
  studyCompleted: false,
  sourceFilesComplete: false,
  technicalConfirmation: false,
  deliveryComment: '',
  procedureStatus: 'PENDIENTE',
  nonPerformanceReason: '',
  startedAt: '',
  endedAt: '',
  connectivityType: 'NO_APLICA',
  profusionPatientCode: '',
  profusionStudyCode: '',
  dynamicValues: {},
  additionalFields: [],
};
function readAdditionalFields(value: unknown) {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const title = 'title' in item ? String(item.title) : '';
    const description = 'description' in item ? String(item.description) : '';
    return [{ title, description }];
  });
}
function localDateTimeValue(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}
const finalProcedureStatuses = ['REALIZADO', 'INCOMPLETO', 'NO_REALIZADO'];
export function TechnicalPage() {
  const id = Number(useParams().id);
  const navigate = useNavigate();
  const client = useQueryClient();
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ['study', id],
    queryFn: () => api<StudyDetail>(`/api/studies/${id}`),
  });
  const [form, setForm] = useState<TechnicalForm>({ ...blank });
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => {
    const s = query.data;
    const c = s?.technicalCapture as Record<string, unknown> | null;
    if (s)
      setForm({
        weightKg: c?.weightKg == null ? '' : String(c.weightKg),
        heightCm: c?.heightCm == null ? '' : String(c.heightCm),
        systolicBloodPressure:
          c?.systolicBloodPressure == null
            ? ''
            : String(c.systolicBloodPressure),
        diastolicBloodPressure:
          c?.diastolicBloodPressure == null
            ? ''
            : String(c.diastolicBloodPressure),
        heartRateBpm: c?.heartRateBpm == null ? '' : String(c.heartRateBpm),
        respiratoryRateBpm:
          c?.respiratoryRateBpm == null ? '' : String(c.respiratoryRateBpm),
        temperatureC: c?.temperatureC == null ? '' : String(c.temperatureC),
        patientCondition: String(c?.patientCondition ?? ''),
        relevantHistory: String(c?.relevantHistory ?? ''),
        technicalObservations: String(c?.technicalObservations ?? ''),
        procedureIncidents: String(c?.procedureIncidents ?? ''),
        studyStarted: Boolean(c?.studyStarted),
        studyCompleted: Boolean(c?.studyCompleted),
        sourceFilesComplete: Boolean(
          c?.sourceFilesComplete ?? s.sourceFilesComplete,
        ),
        technicalConfirmation: Boolean(c?.technicalConfirmation),
        deliveryComment: String(c?.deliveryComment ?? ''),
        procedureStatus: String(s.procedureStatus),
        nonPerformanceReason: String(s.nonPerformanceReason ?? ''),
        startedAt: localDateTimeValue(s.startedAt),
        endedAt: localDateTimeValue(s.endedAt),
        connectivityType: s.connectivityType,
        profusionPatientCode: String(s.profusionPatientCode ?? ''),
        profusionStudyCode: String(s.profusionStudyCode ?? ''),
        dynamicValues: s.dynamicValues ?? {},
        additionalFields: readAdditionalFields(c?.additionalFields),
      });
  }, [query.data]);
  const set = <K extends keyof TechnicalForm>(
    key: K,
    value: TechnicalForm[K],
  ) => setForm((current) => ({ ...current, [key]: value }));
  const mutation = useMutation({
    mutationFn: async (intent: SaveIntent) => {
      const procedureFinalized = finalProcedureStatuses.includes(form.procedureStatus);
      await api(`/api/studies/${id}/technical-capture`, {
        method: 'PUT',
        ...jsonBody({
          ...form,
          studyStarted: form.procedureStatus !== 'PENDIENTE',
          studyCompleted: procedureFinalized,
          technicalConfirmation: procedureFinalized,
          weightKg: form.weightKg ? Number(form.weightKg) : null,
          heightCm: form.heightCm ? Number(form.heightCm) : null,
          systolicBloodPressure: form.systolicBloodPressure
            ? Number(form.systolicBloodPressure)
            : null,
          diastolicBloodPressure: form.diastolicBloodPressure
            ? Number(form.diastolicBloodPressure)
            : null,
          heartRateBpm: form.heartRateBpm ? Number(form.heartRateBpm) : null,
          respiratoryRateBpm: form.respiratoryRateBpm
            ? Number(form.respiratoryRateBpm)
            : null,
          temperatureC: form.temperatureC ? Number(form.temperatureC) : null,
          startedAt: form.startedAt ? new Date(form.startedAt) : null,
          endedAt: form.endedAt ? new Date(form.endedAt) : null,
          patientCondition: form.patientCondition || null,
          relevantHistory: form.relevantHistory || null,
          procedureIncidents: form.procedureIncidents || null,
          deliveryComment: form.deliveryComment || null,
          nonPerformanceReason: form.nonPerformanceReason || null,
          profusionPatientCode: form.profusionPatientCode || null,
          profusionStudyCode: form.profusionStudyCode || null,
        }),
      });

      if (intent === 'draft') return { intent };

      let current = await api<StudyDetail>(`/api/studies/${id}`);
      if (current.stateCode === 'EST_EN_PROCESO') {
        current = {
          ...current,
          ...(await api<Partial<StudyDetail>>(`/api/studies/${id}/transitions`, {
            method: 'POST',
            ...jsonBody({
              actionCode: 'FINALIZAR_ESTUDIO',
              comment: null,
              expectedStateCode: current.stateCode,
              expectedRowVersion: current.rowVersion,
            }),
          })),
        };
      }

      if (current.stateCode !== 'EST_REALIZADO') {
        throw new Error('El estudio no quedó listo para enviarse a revisión médica.');
      }

      await api<Partial<StudyDetail>>(`/api/studies/${id}/transitions`, {
        method: 'POST',
        ...jsonBody({
          actionCode: 'ENVIAR_REVISION',
          comment: null,
          expectedStateCode: current.stateCode,
          expectedRowVersion: current.rowVersion,
        }),
      });
      return { intent };
    },
    onSuccess: async ({ intent }) => {
      setMessage(
        intent === 'complete'
          ? 'Captura finalizada y enviada a revisión médica.'
          : 'Borrador de captura guardado.',
      );
      await Promise.all([
        client.invalidateQueries({ queryKey: ['study', id] }),
        client.invalidateQueries({ queryKey: ['transitions', id] }),
        client.invalidateQueries({ queryKey: ['studies'] }),
      ]);
      if (intent === 'complete') navigate(`/studies/${id}`);
    },
    onError: (e) =>
      setMessage(e instanceof Error ? e.message : 'Error al guardar.'),
  });
  if (query.isLoading) return <Loading />;
  if (query.error || !query.data)
    return <Notice kind="error">No fue posible cargar el estudio.</Notice>;
  const s = query.data;
  const configuredFields = s.studyType.fields.filter(
    (field) =>
      field.active &&
      (!field.appliesToRole || !user?.role || field.appliesToRole === user.role),
  );
  const canComplete = ['EST_EN_PROCESO', 'EST_REALIZADO'].includes(s.stateCode);
  const technicalReturn = s.history.find(
    (event) => event.actionCode === 'DEVOLVER_TECNICA',
  );
  return (
    <>
      <PageHeader
        title={`Captura técnica · ${s.code}`}
        description="Datos técnicos, Profusion, evidencias y preparación para revisión médica."
        actions={<StatusBadge value={s.stateCode} />}
      />
      {message && (
        <Notice
          kind={/guardado|enviada/.test(message) ? 'success' : 'error'}
          onClose={() => setMessage(null)}
        >
          {message}
        </Notice>
      )}
      {s.stateCode === 'EST_EN_PROCESO' && technicalReturn && (
        <Notice kind="info">
          Devuelto por el médico:{' '}
          {technicalReturn.comment || 'Revise y complete la captura técnica.'}
        </Notice>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const submitter = (e.nativeEvent as SubmitEvent).submitter as
            | HTMLButtonElement
            | null;
          mutation.mutate(submitter?.value === 'complete' ? 'complete' : 'draft');
        }}
      >
        <Card title="Procedimiento">
          <div className="form-grid three">
            <Field label="Estado">
              <select
                value={form.procedureStatus}
                onChange={(e) => set('procedureStatus', e.target.value)}
              >
                {[
                  'PENDIENTE',
                  'EN_PROCESO',
                  'REALIZADO',
                  'INCOMPLETO',
                  'NO_REALIZADO',
                ].map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </Field>
            <Field label="Inicio">
              <input
                type="datetime-local"
                value={form.startedAt}
                onChange={(e) => set('startedAt', e.target.value)}
              />
            </Field>
            <Field label="Finalización">
              <input
                type="datetime-local"
                value={form.endedAt}
                onChange={(e) => set('endedAt', e.target.value)}
              />
            </Field>
            <Field label="Peso (kg)" required={s.studyType.requiresWeight}>
              <input
                type="number"
                step="0.01"
                value={form.weightKg}
                onChange={(e) => set('weightKg', e.target.value)}
              />
            </Field>
            <Field label="Talla (cm)" required={s.studyType.requiresHeight}>
              <input
                type="number"
                step="0.01"
                value={form.heightCm}
                onChange={(e) => set('heightCm', e.target.value)}
              />
            </Field>
            <Field label="Presión sistólica (mmHg)">
              <input
                type="number"
                step="0.01"
                value={form.systolicBloodPressure}
                onChange={(e) => set('systolicBloodPressure', e.target.value)}
              />
            </Field>
            <Field label="Presión diastólica (mmHg)">
              <input
                type="number"
                step="0.01"
                value={form.diastolicBloodPressure}
                onChange={(e) => set('diastolicBloodPressure', e.target.value)}
              />
            </Field>
            <Field label="Frecuencia cardiaca (lpm)">
              <input
                type="number"
                step="0.01"
                value={form.heartRateBpm}
                onChange={(e) => set('heartRateBpm', e.target.value)}
              />
            </Field>
            <Field label="Frecuencia respiratoria (rpm)">
              <input
                type="number"
                step="0.01"
                value={form.respiratoryRateBpm}
                onChange={(e) => set('respiratoryRateBpm', e.target.value)}
              />
            </Field>
            <Field label="Temperatura (°C)">
              <input
                type="number"
                step="0.01"
                value={form.temperatureC}
                onChange={(e) => set('temperatureC', e.target.value)}
              />
            </Field>
            <Field label="Motivo del resultado incompleto o no realizado">
              <input
                value={form.nonPerformanceReason}
                onChange={(e) => set('nonPerformanceReason', e.target.value)}
              />
            </Field>
            <Field label="Conectividad">
              <select
                value={form.connectivityType}
                onChange={(e) =>
                  set('connectivityType', e.target.value as ConnectivityType)
                }
              >
                {[
                  'NO_APLICA',
                  'WIFI_CLINICA',
                  'ETHERNET',
                  'HOTSPOT_PERSONAL',
                  'SIM_CORPORATIVA',
                  'MIFI',
                  'SIN_CONECTIVIDAD',
                  'OTRO',
                ].map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </Field>
            <Field label="Código paciente Profusion">
              <input
                value={form.profusionPatientCode}
                onChange={(e) => set('profusionPatientCode', e.target.value)}
              />
            </Field>
            <Field label="Código estudio Profusion">
              <input
                value={form.profusionStudyCode}
                onChange={(e) => set('profusionStudyCode', e.target.value)}
              />
            </Field>
          </div>
        </Card>
        <Card title="Información técnica">
          <div className="form-grid two">
            <Field label="Condición del paciente">
              <textarea
                rows={4}
                value={form.patientCondition}
                onChange={(e) => set('patientCondition', e.target.value)}
              />
            </Field>
            <Field label="Antecedentes relevantes">
              <textarea
                rows={4}
                value={form.relevantHistory}
                onChange={(e) => set('relevantHistory', e.target.value)}
              />
            </Field>
            <Field label="Observaciones técnicas" required>
              <textarea
                required
                rows={6}
                value={form.technicalObservations}
                onChange={(e) => set('technicalObservations', e.target.value)}
              />
            </Field>
            <Field label="Incidentes del procedimiento">
              <textarea
                rows={6}
                value={form.procedureIncidents}
                onChange={(e) => set('procedureIncidents', e.target.value)}
              />
            </Field>
            <Field label="Comentario de entrega">
              <textarea
                rows={4}
                value={form.deliveryComment}
                onChange={(e) => set('deliveryComment', e.target.value)}
              />
            </Field>
          </div>
        </Card>
        {configuredFields.length > 0 && (
          <Card title="Información específica del tipo de estudio">
            <DynamicFields
              fields={configuredFields}
              values={form.dynamicValues}
              onChange={(code, value) =>
                set('dynamicValues', { ...form.dynamicValues, [code]: value })
              }
              role={user?.role}
            />
          </Card>
        )}
        <Card
          title="Información adicional del caso"
          actions={<Button type="button" variant="secondary" disabled={form.additionalFields.length>=20} onClick={()=>set('additionalFields',[...form.additionalFields,{title:'',description:''}])}>Agregar campo</Button>}
        >
          <p className="muted">Agregue información propia de este caso cuando no esté cubierta por los campos anteriores. No se reutilizará en otros estudios.</p>
          <div className="compact-list">
            {form.additionalFields.map((field,index)=><div className="form-grid two" key={index}>
              <Field label={`Título ${index+1}`} required><input required maxLength={120} value={field.title} onChange={(event)=>set('additionalFields',form.additionalFields.map((item,itemIndex)=>itemIndex===index?{...item,title:event.target.value}:item))}/></Field>
              <Field label={`Descripción ${index+1}`} required><textarea required rows={4} maxLength={8000} value={field.description} onChange={(event)=>set('additionalFields',form.additionalFields.map((item,itemIndex)=>itemIndex===index?{...item,description:event.target.value}:item))}/></Field>
              <Button type="button" variant="danger" onClick={()=>set('additionalFields',form.additionalFields.filter((_,itemIndex)=>itemIndex!==index))}>Eliminar campo</Button>
            </div>)}
            {form.additionalFields.length===0&&<p className="muted">Agregue un campo solo cuando este caso requiera información que no aparece arriba.</p>}
          </div>
        </Card>
        <div className="sticky-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(`/studies/${id}`)}
          >
            Volver
          </Button>
          <Button type="submit" name="saveIntent" value="draft" variant="secondary" busy={mutation.isPending}>
            Guardar borrador
          </Button>
          {canComplete && (
            <Button
              type="submit"
              name="saveIntent"
              value="complete"
              busy={mutation.isPending}
              disabled={!finalProcedureStatuses.includes(form.procedureStatus)}
              title={
                finalProcedureStatuses.includes(form.procedureStatus)
                  ? undefined
                  : 'Seleccione REALIZADO, INCOMPLETO o NO REALIZADO para finalizar.'
              }
            >
              Finalizar y enviar al médico
            </Button>
          )}
        </div>
      </form>
    </>
  );
}
